import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import {
  Palmtree, Sparkles, ShieldCheck, Lock, ThumbsUp, CreditCard,
  Star, MapPin, Check, X, Copy, Loader2, BedDouble, Bath,
  Users, Wifi, Waves, Instagram, Facebook, MessageCircle,
  CalendarDays, Phone, Mail, UserRound
} from "lucide-react";
import { useSite, waLink, BRAND } from "./lib/site";
import { PhotoLightbox } from "./components/PhotoLightbox";
import { PixPayment } from "./components/PixPayment";
import { GuestFields, TermsCheckbox, EMPTY_GUEST, guestError, guestPayload } from "./components/GuestFields";
import { ToursSection, useTours } from "./components/ToursSection";
import { TourExtras, extrasTotals } from "./components/TourExtras";
import { CouponField, couponDiscount } from "./components/CouponField";
import { AddonsPicker, addonsTotal } from "./components/AddonsPicker";

const WA_GENERAL = "Olá, equipe Se Hospede AKI! Encontrei vocês pelo site e gostaria de tirar algumas dúvidas sobre hospedagem:\n\n• Disponibilidade de datas\n• Valores e formas de pagamento\n• O que está incluso na estadia\n\nPodem me ajudar? Obrigado(a)!";
import { Images, ChevronLeft, ChevronRight } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { AvailabilityCalendar } from "./components/AvailabilityCalendar";
import { ReviewsSection } from "./components/ReviewsSection";
import { stayPrice, mediaUrl } from "./lib/pricing";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BRL = (v) =>
  (Number(v) || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
  });

const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};
const today = () => new Date().toLocaleDateString("sv-SE");
const fmtBR = (s) => s.split("-").reverse().join("/");

// ----- Hero image fallback -----
const HERO_IMG =
  "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1600";

// ============================================================
// Public landing — Se Hospede AKI
// ============================================================
export default function PublicSite() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // property for booking modal
  const tours = useTours();

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/public/properties`);
      setProperties(r.data || []);
    } catch (e) {
      toast.error("Erro ao carregar imóveis.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" style={{ fontFamily: "Outfit, system-ui, sans-serif" }}>
      <Toaster position="top-right" richColors />
      <Nav />
      <Hero propertiesCount={properties.length} />
      <TrustBar />
      <PropertiesSection
        properties={properties}
        loading={loading}
        onReserve={setSelected}
      />
      <ToursSection tours={tours} />
      <HowItWorks />
      <ReviewsSection />
      <Footer />
      <WhatsAppFab />
      {selected && (
        <BookingModal
          property={selected}
          tours={tours}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

// ----------------------------------------------------------- Nav
function Nav() {
  return (
    <nav className="fixed top-0 inset-x-0 z-40 border-b border-slate-200" style={{ background: "rgba(255,255,255,0.85)", backdropFilter: "blur(14px)" }}>
      <div className="max-w-7xl mx-auto flex justify-between items-center px-4 py-3">
        <a href="#top" className="flex items-center gap-2 group" data-testid="nav-logo">
          <div className="bg-[#0A66C2] p-2 rounded-xl shadow-lg group-hover:rotate-6 transition-transform">
            <Palmtree className="text-white w-6 h-6" />
          </div>
          <span className="text-xl sm:text-2xl font-extrabold tracking-tight text-[#0A66C2] whitespace-nowrap">
            Se Hospede <span className="text-[#FFB800]">AKI</span>
          </span>
        </a>
        <div className="hidden md:flex items-center gap-8 font-semibold text-slate-600">
          <a href="#imoveis" className="hover:text-[#0A66C2] transition-colors">Imóveis</a>
          <a href="#passeios" className="hover:text-[#0A66C2] transition-colors">Passeios de Jet Ski</a>
          <a href="#como-funciona" className="hover:text-[#0A66C2] transition-colors">Como funciona</a>
          <a href="#depoimentos" className="hover:text-[#0A66C2] transition-colors">Avaliações</a>
        </div>
        <a
          href="#imoveis"
          className="bg-[#FFCC00] text-blue-900 px-4 sm:px-5 py-2.5 rounded-full font-bold text-sm hover:bg-yellow-400 transition-[background-color,box-shadow] shadow-md hover:shadow-lg whitespace-nowrap"
          data-testid="nav-cta"
        >
          Reservar
        </a>
      </div>
    </nav>
  );
}

// ----------------------------------------------------------- Hero
function Hero({ propertiesCount }) {
  return (
    <section id="top" className="relative pt-28 pb-16 px-4 overflow-hidden">
      <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-12">
        <div className="lg:w-1/2 text-center lg:text-left z-10">
          <div className="inline-flex items-center gap-2 bg-blue-100 text-[#0A66C2] px-4 py-1.5 rounded-full text-xs font-bold uppercase mb-6">
            <Sparkles className="w-3 h-3" /> Temporada de verão — datas abertas
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mb-6 leading-[1.1] text-slate-900">
            Sua casa de temporada <span className="text-[#0A66C2]">sem complicação</span>, com reserva garantida.
          </h1>
          <p className="text-base sm:text-lg text-slate-600 mb-10 max-w-xl mx-auto lg:mx-0 leading-relaxed">
            Alugue direto com o anfitrião, sem intermediários. {propertiesCount > 0 ? `${propertiesCount} ${propertiesCount === 1 ? "imóvel disponível" : "imóveis disponíveis"}, ` : ""}calendário atualizado em tempo real e confirmação automática após o PIX.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start items-center">
            <a
              href="#imoveis"
              className="bg-[#FFCC00] text-blue-900 px-10 py-4 rounded-2xl text-lg font-extrabold text-center shadow-[0_4px_0_#b38f00] active:translate-y-1 active:shadow-none transition-all"
              data-testid="hero-cta"
            >
              Ver imóveis disponíveis
            </a>
            <div className="flex items-center gap-3 text-slate-500 font-medium px-4">
              <ShieldCheck className="text-green-500 w-6 h-6" />
              <span>Pagamento via PIX com confirmação automática</span>
            </div>
          </div>
        </div>
        <div className="lg:w-1/2 relative">
          <div className="absolute -top-10 -right-10 w-64 h-64 bg-[#FFCC00]/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-10 -left-10 w-64 h-64 bg-blue-400/20 rounded-full blur-3xl" />
          <img
            src={HERO_IMG}
            className="relative rounded-[2.5rem] shadow-2xl border-[10px] border-white w-full"
            alt="Casa à beira-mar"
          />
          <div className="absolute -bottom-6 -right-6 bg-white p-5 rounded-3xl shadow-2xl border border-slate-100 hidden sm:block">
            <div className="flex items-center gap-3">
              <div className="bg-green-100 p-2 rounded-full">
                <Check className="text-green-600 w-5 h-5" />
              </div>
              <div>
                <p className="text-[11px] text-slate-400 font-bold uppercase">Reserva</p>
                <p className="text-sm font-extrabold text-slate-800">Confirmada na hora</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ----------------------------------------------------------- Trust Bar
function TrustBar() {
  const items = [
    { icon: Lock, label: "Conexão segura (HTTPS)" },
    { icon: CreditCard, label: "Pagamento via PIX" },
    { icon: ThumbsUp, label: "Atendimento direto com o anfitrião" },
  ];
  return (
    <div id="seguranca" className="bg-white py-10 border-y border-slate-200">
      <div className="max-w-7xl mx-auto px-4 flex flex-wrap justify-center gap-6 md:gap-16 text-slate-500 font-bold text-sm uppercase tracking-wider">
        {items.map(({ icon: Icon, label }) => (
          <div key={label} className="flex items-center gap-2 hover:text-[#0A66C2] transition-colors cursor-default">
            <Icon className="w-5 h-5" /> {label}
          </div>
        ))}
      </div>
    </div>
  );
}

// ----------------------------------------------------------- Properties grid
function PropertiesSection({ properties, loading, onReserve }) {
  return (
    <section id="imoveis" className="py-20 max-w-7xl mx-auto px-4">
      <div className="text-center mb-14">
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-4 text-slate-900">
          Imóveis disponíveis
        </h2>
        <p className="text-slate-500 text-base sm:text-lg max-w-2xl mx-auto">
          Escolha o imóvel ideal para a sua próxima temporada. Reserve com antecedência: as datas mais procuradas esgotam rápido.
        </p>
      </div>
      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="w-10 h-10 animate-spin text-[#0A66C2]" />
        </div>
      ) : properties.length === 0 ? (
        <div className="text-center py-20 text-slate-500">
          <p className="text-lg">Nenhum imóvel disponível no momento.</p>
          <p className="text-sm mt-2">Novas opções serão publicadas em breve.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {properties.map((p) => (
            <PropertyCard key={p.id} p={p} onReserve={() => onReserve(p)} />
          ))}
        </div>
      )}
    </section>
  );
}

function PropertyCard({ p, onReserve }) {
  const photos = p.images?.length ? p.images : [HERO_IMG];
  const [idx, setIdx] = useState(0);
  const [gallery, setGallery] = useState(false);
  const step = (d) => (e) => { e.stopPropagation(); setIdx((x) => (x + d + photos.length) % photos.length); };
  const highlightColors = {
    "Mais procurada": "bg-[#0A66C2] text-white",
    "Última data!": "bg-red-600 text-white",
  };
  const hlClass = highlightColors[p.highlight] || "bg-slate-900 text-white";

  return (
    <div
      className="bg-white rounded-[2rem] overflow-hidden shadow-sm border border-slate-200 group hover:-translate-y-1 hover:shadow-2xl transition-[transform,box-shadow] duration-300"
      data-testid={`property-card-${p.id}`}
    >
      {gallery && <PhotoLightbox images={photos} title={p.title} start={idx} onClose={() => setGallery(false)} />}
      <div className="relative h-64 overflow-hidden cursor-zoom-in" onClick={() => setGallery(true)} data-testid={`card-photo-${p.id}`}>
        <img
          src={mediaUrl(photos[idx])}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          alt={`${p.title} — foto ${idx + 1}`}
          loading="lazy"
        />
        {photos.length > 1 && (
          <>
            <button onClick={step(-1)} aria-label="Foto anterior" data-testid={`card-prev-${p.id}`}
              className="absolute left-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/90 text-slate-800 shadow flex items-center justify-center opacity-100 md:opacity-0 group-hover:opacity-100 transition-opacity">
              <ChevronLeft size={18} />
            </button>
            <button onClick={step(1)} aria-label="Próxima foto" data-testid={`card-next-${p.id}`}
              className="absolute right-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/90 text-slate-800 shadow flex items-center justify-center opacity-100 md:opacity-0 group-hover:opacity-100 transition-opacity">
              <ChevronRight size={18} />
            </button>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
              {photos.slice(0, 8).map((_, j) => (
                <span key={j} className={`h-1.5 rounded-full transition-[width,background-color] ${j === idx ? "w-4 bg-white" : "w-1.5 bg-white/60"}`} />
              ))}
            </div>
          </>
        )}
        <button onClick={(e) => { e.stopPropagation(); setGallery(true); }} data-testid={`card-gallery-${p.id}`}
          className="absolute bottom-3 right-3 bg-slate-900/75 hover:bg-slate-900 text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5 backdrop-blur-sm">
          <Images size={14} /> {photos.length === 1 ? "Ver foto" : `Ver ${photos.length} fotos`}
        </button>
        {p.highlight && (
          <div className={`absolute top-4 left-4 ${hlClass} px-3 py-1 rounded-full text-xs font-bold uppercase shadow-lg`}>
            {p.highlight}
          </div>
        )}
      </div>
      <div className="p-6">
        <div className="flex justify-between items-start mb-2 gap-3">
          <h3 className="text-xl font-bold text-slate-800 leading-tight"><a href={`/casa/${p.id}`} className="hover:text-[#0A66C2]" data-testid={`property-link-${p.id}`}>{p.title}</a></h3>
          <div className="flex items-center gap-1 text-yellow-500 font-bold bg-yellow-50 px-2 py-1 rounded-lg shrink-0">
            <Star className="w-4 h-4 fill-current" /> {Number(p.rating || 5).toFixed(1)}
          </div>
        </div>
        <p className="text-slate-500 text-sm mb-4 flex items-center gap-2">
          <MapPin className="w-4 h-4 text-[#0A66C2]" /> {p.city}, {p.state}
        </p>
        <div className="flex gap-4 text-slate-500 text-xs mb-5 flex-wrap">
          <span className="flex items-center gap-1"><Users className="w-4 h-4" /> até {p.max_guests} {p.max_guests === 1 ? "hóspede" : "hóspedes"}</span>
          <span className="flex items-center gap-1"><BedDouble className="w-4 h-4" /> {p.bedrooms} {p.bedrooms === 1 ? "quarto" : "quartos"}</span>
          <span className="flex items-center gap-1"><Bath className="w-4 h-4" /> {p.bathrooms} {p.bathrooms === 1 ? "banheiro" : "banheiros"}</span>
        </div>
        {p.description && <CardDescription id={p.id} text={p.description} />}
        <a href={`/casa/${p.id}`} className="inline-block text-sm font-bold text-[#0A66C2] mb-5 hover:underline" data-testid={`property-details-${p.id}`}>Ver página completa e fotos →</a>
        <div className="flex items-center justify-between pt-5 border-t border-slate-100">
          <div>
            <span className="text-slate-400 text-[11px] block font-bold uppercase">Diária a partir de</span>
            <span className="text-2xl font-extrabold text-[#0A66C2]">{BRL(p.price_per_night)}</span>
          </div>
          <button
            onClick={onReserve}
            className="bg-[#0A66C2] text-white px-6 py-3 rounded-2xl font-bold hover:bg-blue-700 transition-all shadow-lg hover:shadow-blue-200"
            data-testid={`reserve-btn-${p.id}`}
          >
            Reservar
          </button>
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------- How it works
function HowItWorks() {
  const steps = [
    { n: 1, title: "Escolha o imóvel", desc: "Veja as fotos, as comodidades e o calendário com as datas livres." },
    { n: 2, title: "Pague o sinal via PIX", desc: "Garanta as datas pagando só o sinal pelo site. O restante é pago no check-in." },
    { n: 3, title: "Aproveite a estadia", desc: "Após a confirmação, entraremos em contato pelo WhatsApp com as orientações de check-in." },
  ];
  return (
    <section id="como-funciona" className="py-20 bg-white scroll-mt-20">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-14">
          <h2 className="text-3xl sm:text-4xl font-extrabold mb-4 text-slate-900">Como funciona</h2>
          <p className="text-slate-500 text-base max-w-xl mx-auto">
            Em três passos você garante sua hospedagem para o próximo feriado ou para as férias.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {steps.map((s) => (
            <div key={s.n} className="bg-slate-50 border border-slate-100 p-8 rounded-3xl hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 rounded-2xl bg-[#0A66C2] text-white flex items-center justify-center text-2xl font-extrabold mb-5 shadow-lg">
                {s.n}
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">{s.title}</h3>
              <p className="text-slate-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CardDescription({ id, text }) {
  const [open, setOpen] = useState(false);
  const long = text.length > 110;
  return (
    <div className="mb-2">
      <p className={`text-slate-500 text-sm ${open ? "" : "line-clamp-2"}`} data-testid={`property-desc-${id}`}>{text}</p>
      {long && (
        <button onClick={() => setOpen(!open)} className="text-sm font-bold text-slate-700 underline underline-offset-2 mt-1" data-testid={`property-more-${id}`}>
          {open ? "Ver menos" : "Ver mais"}
        </button>
      )}
    </div>
  );
}

// ----------------------------------------------------------- Footer
function Footer() {
  const site = useSite();
  const wa = waLink(site.whatsapp, WA_GENERAL);
  return (
    <footer className="bg-[#0A66C2] text-white pt-14 pb-8" data-testid="site-footer">
      <div className="max-w-6xl mx-auto px-4 grid gap-10 md:grid-cols-3">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Palmtree className="w-7 h-7" />
            <span className="text-2xl font-extrabold">Se Hospede <span className="text-[#FFCC00]">AKI</span></span>
          </div>
          <p className="text-blue-100 text-sm leading-relaxed max-w-xs">
            Casas de temporada com reserva online, calendário atualizado e pagamento via PIX.
          </p>
        </div>
        <div>
          <h3 className="font-bold text-sm uppercase tracking-wider text-blue-200 mb-4">Atendimento</h3>
          <ul className="space-y-2.5 text-sm">
            {site.whatsapp && <li><a href={wa} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-[#FFCC00]" data-testid="footer-whatsapp"><MessageCircle size={16} /> {site.whatsapp}</a></li>}
            {site.email && <li><a href={`mailto:${site.email}`} className="flex items-center gap-2 hover:text-[#FFCC00]" data-testid="footer-email"><Mail size={16} /> {site.email}</a></li>}
            {site.instagram && <li><a href={`https://instagram.com/${site.instagram}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-[#FFCC00]" data-testid="footer-instagram"><Instagram size={16} /> @{site.instagram}</a></li>}
            {!site.whatsapp && !site.email && !site.instagram && <li className="text-blue-100">Fale conosco pelo WhatsApp informado na sua reserva.</li>}
          </ul>
        </div>
        <div>
          <h3 className="font-bold text-sm uppercase tracking-wider text-blue-200 mb-4">Informações</h3>
          <ul className="space-y-2.5 text-sm">
            <li><a href="/termos" className="hover:text-[#FFCC00]" data-testid="footer-terms">Termos de Uso</a></li>
            <li><a href="/privacidade" className="hover:text-[#FFCC00]" data-testid="footer-privacy">Política de Privacidade</a></li>
            <li><a href="/cancelamento" className="hover:text-[#FFCC00]" data-testid="footer-cancel">Política de Cancelamento</a></li>
          </ul>
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-4 mt-12 pt-6 border-t border-white/15 flex flex-col sm:flex-row gap-3 justify-between text-xs text-blue-100/80">
        <p>© {new Date().getFullYear()} {site.company_name || BRAND}{site.cnpj ? ` · CNPJ ${site.cnpj}` : ""}. Todos os direitos reservados.</p>
        <a href="/admin" className="hover:text-white" data-testid="footer-admin">Área do anfitrião</a>
      </div>
    </footer>
  );
}

function WhatsAppFab() {
  const site = useSite();
  const wa = waLink(site.whatsapp, WA_GENERAL);
  if (!wa) return null;
  return (
    <a href={wa} target="_blank" rel="noreferrer" aria-label="Falar no WhatsApp" data-testid="whatsapp-fab"
      className="fixed bottom-5 right-5 z-30 h-14 w-14 rounded-full bg-[#25D366] text-white shadow-xl flex items-center justify-center hover:scale-105 transition-transform">
      <MessageCircle size={26} />
    </a>
  );
}

// ----------------------------------------------------------- Booking Modal
export function BookingModal({ property, tours, onClose }) {
  const [checkin, setCheckin] = useState(today());
  const [checkout, setCheckout] = useState(addDays(new Date(), 3));
  const [form, setForm] = useState(EMPTY_GUEST);
  const [extras, setExtras] = useState([]);
  const [stage, setStage] = useState("form"); // form | pix | confirmed
  const [booking, setBooking] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [booked, setBooked] = useState([]);
  const [accepted, setAccepted] = useState(false);
  const [galleryAt, setGalleryAt] = useState(null);
  const [coupon, setCoupon] = useState(null);
  const [addons, setAddons] = useState([]);
  const [picked, setPicked] = useState([]);

  useEffect(() => {
    axios.get(`${API}/public/addons`).then((r) => setAddons(r.data || [])).catch(() => {});
  }, []);
  const site = useSite();
  const comboPct = Number(site.combo_discount_percent || 0);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !document.querySelector('[data-testid="photo-lightbox"]') && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);

  useEffect(() => {
    axios.get(`${API}/public/properties/${property.id}/booked`).then((r) => setBooked(r.data)).catch(() => {});
  }, [property.id]);

  const clash = booked.find((b) => checkin < b.checkout && checkout > b.checkin);

  const nights = Math.max(
    0,
    Math.round((new Date(checkout) - new Date(checkin)) / 86400000)
  );
  const { total: stayTotal, special } = stayPrice(property, checkin, checkout);
  const depositPct = Number(property.deposit_percent ?? 50);
  const validExtras = extras.filter((x) => x.date >= checkin && x.date <= checkout);
  const ex = extrasTotals(tours, validExtras, comboPct);
  const adds = addonsTotal(addons, picked, nights);
  const grossTotal = stayTotal + adds + ex.total;
  const grossDeposit = ((stayTotal + adds) * depositPct) / 100 + ex.deposit;
  const discount = coupon && ["all", "stay"].includes(coupon.applies_to || "all") ? couponDiscount(coupon, grossTotal) : 0;
  const total = grossTotal - discount;
  const deposit = grossTotal > 0 ? (grossDeposit * total) / grossTotal : 0;

  const submit = async () => {
    if (nights <= 0) {
      toast.error("A data de saída deve ser depois da entrada.");
      return;
    }
    if (clash) {
      toast.error("Essas datas já estão reservadas.");
      return;
    }
    const err = guestError(form, accepted);
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/public/bookings`, {
        property_id: property.id,
        ...guestPayload(form),
        checkin,
        checkout,
        extras: validExtras,
        addons: picked,
        coupon_code: coupon?.code || null,
      });
      setBooking(r.data);
      setStage("pix");
      toast.success("PIX gerado! Pague em até 30 minutos.");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Não foi possível gerar o PIX. Confira os dados e tente novamente.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex p-4 bg-slate-900/70 backdrop-blur-md overflow-y-auto"
      onClick={onClose}
      data-testid="booking-modal"
    >
      {galleryAt !== null && (
        <div onClick={(e) => e.stopPropagation()}>
          <PhotoLightbox images={property.images} title={property.title} start={galleryAt} onClose={() => setGalleryAt(null)} />
        </div>
      )}
      <div
        className="bg-white w-full max-w-md rounded-[2rem] shadow-2xl overflow-hidden m-auto shrink-0"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-[#0A66C2] p-6 text-white flex justify-between items-start gap-4">
          <div>
            <p className="text-blue-200 text-[11px] font-bold uppercase mb-1">
              {stage === "confirmed" ? "Reserva confirmada" : stage === "pix" ? "Pagamento via PIX" : stage === "expired" ? "PIX expirado" : "Nova reserva"}
            </p>
            <h3 className="text-xl font-extrabold leading-tight">{property.title}</h3>
            <p className="text-blue-100 text-sm mt-1">
              <MapPin className="w-3 h-3 inline mr-1" />
              {property.city}, {property.state}
            </p>
          </div>
          <button
            onClick={onClose}
            className="bg-white/20 p-2 rounded-full hover:bg-white/30 transition-colors shrink-0"
            aria-label="Fechar"
            data-testid="modal-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {stage === "form" && (
            <>
              {property.images?.length > 0 && (
                <div className="mb-4" data-testid="modal-gallery">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] font-bold text-slate-500 uppercase">Fotos do imóvel</span>
                    <button onClick={() => setGalleryAt(0)} className="text-xs font-bold text-[#0A66C2] flex items-center gap-1" data-testid="modal-gallery-open">
                      <Images size={13} /> Ver todas ({property.images.length})
                    </button>
                  </div>
                  <div className="flex gap-2 overflow-x-auto -mx-1 px-1 pb-1">
                    {property.images.map((src, i) => (
                      <button key={i} onClick={() => setGalleryAt(i)} className="shrink-0 rounded-xl overflow-hidden hover:opacity-90 transition-opacity" data-testid={`modal-thumb-${i}`}>
                        <img src={mediaUrl(src)} alt={`${property.title} — foto ${i + 1}`} className="h-20 w-28 object-cover" loading="lazy" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <AvailabilityCalendar
                property={property}
                booked={booked}
                checkin={checkin}
                checkout={nights > 0 ? checkout : null}
                onChange={(ci, co) => {
                  setCheckin(ci);
                  setCheckout(co || ci);
                }}
              />
              <div className="grid grid-cols-2 gap-3 mb-4">
                <Field label="Entrada" icon={CalendarDays}>
                  <input
                    type="date"
                    value={checkin}
                    min={today()}
                    onChange={(e) => {
                      setCheckin(e.target.value);
                      if (new Date(e.target.value) >= new Date(checkout)) {
                        setCheckout(addDays(e.target.value, 1));
                      }
                    }}
                    className="w-full p-2.5 rounded-xl border border-slate-200 outline-none focus:ring-2 focus:ring-[#0A66C2] text-sm"
                    data-testid="input-checkin"
                  />
                </Field>
                <Field label="Saída" icon={CalendarDays}>
                  <input
                    type="date"
                    value={checkout}
                    min={addDays(checkin, 1)}
                    onChange={(e) => setCheckout(e.target.value)}
                    className="w-full p-2.5 rounded-xl border border-slate-200 outline-none focus:ring-2 focus:ring-[#0A66C2] text-sm"
                    data-testid="input-checkout"
                  />
                </Field>
              </div>
              {booked.length > 0 && (
                <div
                  className={`mb-4 p-3 rounded-xl text-xs ${clash ? "bg-red-50 text-red-700 border border-red-200" : "bg-slate-50 text-slate-600"}`}
                  data-testid="booked-dates"
                >
                  <strong>{clash ? "Datas indisponíveis:" : "Datas já reservadas:"}</strong>{" "}
                  {booked.map((b) => `${fmtBR(b.checkin)} → ${fmtBR(b.checkout)}`).join(" · ")}
                </div>
              )}

              <AddonsPicker addons={addons} picked={picked} setPicked={setPicked} nights={nights} />
              <TourExtras tours={tours} checkin={checkin} checkout={checkout} extras={extras} setExtras={setExtras} comboPct={comboPct} />
              <GuestFields form={form} setForm={setForm} />

              <div className="bg-blue-50 p-5 rounded-2xl mb-6 border border-blue-100">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-slate-500">
                    {nights} {nights === 1 ? "noite" : "noites"}{special > 0 ? "" : ` × ${BRL(property.price_per_night)}`}
                  </span>
                  <span className="font-bold text-slate-700" data-testid="stay-total">{BRL(stayTotal)}</span>
                </div>
                {adds > 0 && (
                  <div className="flex justify-between text-sm mb-2" data-testid="addons-total">
                    <span className="text-slate-500">Serviços adicionais</span>
                    <span className="font-bold text-slate-700">{BRL(adds)}</span>
                  </div>
                )}
                {ex.total > 0 && (
                  <div className="flex justify-between text-sm mb-2" data-testid="extras-total">
                    <span className="text-slate-500">Passeios de jet ski{ex.combo > 0 ? ` (combo −${BRL(ex.combo)})` : ""}</span>
                    <span className="font-bold text-slate-700">{BRL(ex.total)}</span>
                  </div>
                )}
                {discount > 0 && (
                  <div className="flex justify-between text-sm mb-2 text-emerald-700" data-testid="coupon-discount">
                    <span>Cupom {coupon.code}</span>
                    <span className="font-bold">−{BRL(discount)}</span>
                  </div>
                )}
                {special > 0 && (
                  <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1 mb-2" data-testid="season-notice">
                    {special === 1 ? "1 noite com tarifa de temporada" : `${special} noites com tarifa de temporada`}
                  </p>
                )}
                <div className="flex justify-between items-end pt-2 border-t border-blue-200">
                  <div>
                    <span className="text-[#0A66C2] text-[11px] font-bold uppercase block">
                      Sinal{ex.total > 0 ? "" : ` (${depositPct}%)`}
                    </span>
                    <span className="text-slate-500 text-xs">Pago agora, via PIX</span>
                  </div>
                  <span className="text-2xl font-extrabold text-[#0A66C2]" data-testid="deposit-amount">
                    {BRL(deposit)}
                  </span>
                </div>
                <div className="flex justify-between text-xs text-slate-500 mt-2" data-testid="remaining-amount">
                  <span>Restante, pago no check-in{ex.total > 0 ? " e no passeio" : ""}</span>
                  <span className="font-semibold">{BRL(total - deposit)}</span>
                </div>
              </div>

              <CouponField kind="stay" total={grossTotal} coupon={coupon} setCoupon={setCoupon} />
              <TermsCheckbox accepted={accepted} setAccepted={setAccepted} />

              <button
                onClick={submit}
                disabled={submitting}
                className="w-full bg-[#FFCC00] text-blue-900 py-4 rounded-2xl font-extrabold text-lg shadow-[0_4px_0_#b38f00] active:translate-y-1 active:shadow-none transition-all flex items-center justify-center gap-3 disabled:opacity-60"
                data-testid="submit-booking"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" /> Gerando PIX...
                  </>
                ) : (
                  <>
                    Pagar sinal com PIX <Lock className="w-5 h-5" />
                  </>
                )}
              </button>
              <p className="text-center text-xs text-slate-400 mt-4 flex items-center justify-center gap-2">
                <ShieldCheck className="w-4 h-4" /> Pagamento processado pela LofyPay · conexão segura
              </p>
            </>
          )}

          {stage !== "form" && (
            <PixPayment stage={stage} booking={booking} onStage={setStage} onClose={onClose}
              onRetry={() => { setBooking(null); setStage("form"); }}
              confirmedText="Sua reserva está garantida. Em breve entraremos em contato pelo WhatsApp informado com as orientações de check-in."
              rows={[
                ["Imóvel", property.title],
                ["Check-in", fmtBR(checkin)],
                ["Check-out", fmtBR(checkout)],
                ...validExtras.map((x) => ["Passeio", `${tours.find((t) => t.id === x.tour_id)?.title || "Jet ski"} · ${fmtBR(x.date)} às ${x.time}`]),
                ["Sinal pago", BRL(booking?.deposit_amount || 0)],
                ["Restante", BRL(Math.max(0, (booking?.total_amount || total) - (booking?.deposit_amount || 0)))],
              ]} />
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <div className="mb-3">
      <label className="text-[11px] font-bold text-slate-500 uppercase mb-1.5 flex items-center gap-1.5">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </label>
      {children}
    </div>
  );
}
